import { isArray, isEmpty, isObject } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { LabelValueItem, toArray } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { FC, Fragment, useEffect, useState } from 'react';
import { Button } from '../button';
import { CheckListSelectedValue } from '../check-list';
import { CheckListItemValue, CheckListValue } from '../check-list/context';
import { Checkbox, CheckboxItem } from '../checkbox';
import { FlexLayout } from '../flex-layout';
import { useEffectCustom } from '../hooks';
import { ModalDrawer } from '../modal-drawer';
import { NativeScroll } from '../native-scroll';
import { getCheckChangeProps } from '../utils/inner-use/check-change';
import { px2rem } from '../utils/pxrem';
import './style.less';

export interface SimpleSelectDataItem {
  value: CheckListItemValue;
  label: string;
  labelElement?: JSX.Element;
}

export type SimpleSelectSelectedParam<T extends 'multi' | 'radio'> = {
  label: string;
  value: T extends 'multi' ? { value: CheckListItemValue; label: string }[] : CheckListItemValue;
};

export interface SimpleSelectProps {
  visible?: boolean;
  className?: string;
  title: string;
  value?: CheckListValue;
  multiple?: boolean;
  dataSource: SimpleSelectDataItem[];
  buttonName?: string | JSX.Element;
  onClose?: () => void;
  onChange?: (value?: SimpleSelectSelectedParam<never>) => void;
  selectType?: 'select' | 'confirm';
  required?: boolean;
  onValueChange?: (dataList?: LabelValueItem[] | LabelValueItem) => void;
}

/**
 * 弹框勾选组件
 * @deprecated 请使用ModalSelector组件替换
 * ```
 * 1. 与Form结合使用赋值与取值方式
 *    例如：
 *    a. 赋值格式: { label: '白金会员', value: '1' }
 *    b. 取值格式:
 *        {
 *          label: '白金会员',
 *          value: '1'
 *        }
 *       可通过使用form.getFormModalPickerValue(value) 直接取值 => '1'
 * ```
 */
export const SimpleSelect: FC<SimpleSelectProps> = (props) => {
  const [selectKeys, setSelectKeys] = useState<CheckListItemValue[]>([]);

  useEffect(() => {
    console.warn('@flatbiz/react库【SimpleSelect】组件已经废弃，请使用【ModalSelector】组件替代');
  }, []);

  const onValueChange = hooks.useCallbackRef((data?: LabelValueItem | LabelValueItem[]) => {
    const targetList = toArray<LabelValueItem>(data);
    if (props.multiple) {
      props.onValueChange?.(targetList);
    } else {
      props.onValueChange?.(targetList[0]);
    }
  });

  useEffectCustom(() => {
    if (!isEmpty(props.value)) {
      const tempValues = toArray(props.value);
      const values = tempValues
        .map((item) => {
          return isObject(item) ? item?.['value'] : item;
        })
        .filter(Boolean);
      const targetList = props.dataSource.filter((item) => values.includes(item.value));
      setSelectKeys(targetList.map((item) => item.value));
      onValueChange(targetList);
    } else {
      onValueChange(undefined);
    }
  }, [props.value, props.dataSource]);

  const onChange = hooks.useCallbackRef((value: CheckListSelectedValue<never>) => {
    setSelectKeys((isArray(value) ? value : value ? [value] : []) as CheckListItemValue[]);
    if (props.selectType === 'select') {
      setTimeout(() => {
        onChangeHandle(value as CheckListValue);
      }, 100);
    }
  });

  const onChangeHandle = hooks.useCallbackRef((value: CheckListValue) => {
    const target = getCheckChangeProps(value, props.dataSource, props.multiple || false);
    props.onChange?.(target as SimpleSelectSelectedParam<never>);
    onValueChange(target);
  });

  const onConfirm = hooks.useCallbackRef(() => {
    onChangeHandle(selectKeys);
  });

  return (
    <ModalDrawer
      closeIcon={true}
      className={classNames('simple-select', props.className)}
      visible={props.visible || false}
      onClose={props.onClose}
      title={props.title}
    >
      <FlexLayout fullIndex={0} height={px2rem(800)} className="simple-select-content">
        <NativeScroll>
          <Checkbox
            className="ss-content"
            onChange={onChange}
            multiple={props.multiple}
            required={props.required}
            value={selectKeys}
          >
            {props.dataSource.map((item, index) => {
              return (
                <CheckboxItem key={index} value={item.value} reverse={true}>
                  <Fragment>{item.labelElement || item.label}</Fragment>
                </CheckboxItem>
              );
            })}
          </Checkbox>
        </NativeScroll>
        {props.selectType !== 'select' && (
          <div className="ss-operate">
            <Button type="primary" onClick={onConfirm}>
              {props.buttonName || '确认'}
            </Button>
          </div>
        )}
      </FlexLayout>
    </ModalDrawer>
  );
};

SimpleSelect.defaultProps = {
  selectType: 'confirm',
};
